import { useState, useMemo } from 'react';
import { Client } from '@/types/client';
import { sampleClients } from '@/data/sampleClients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Search, 
  Menu, 
  Home, 
  Users, 
  BarChart3, 
  Settings, 
  Filter,
  Bell,
  Plus,
  ChevronRight,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  X
} from 'lucide-react';
import { StatusBadge } from '@/components/ui/StatusBadge';

const MobileApp = () => {
  const [clients, setClients] = useState<Client[]>(sampleClients);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientDetailOpen, setClientDetailOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'clients' | 'stats' | 'settings'>('home');

  // Filtered clients
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = 
        client.directorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.emailAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.abn.includes(searchQuery);
      
      const matchesStatus = statusFilter === 'all' || 
        client.basStatus === statusFilter ||
        client.bookkeepingStatus === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [clients, searchQuery, statusFilter]);

  const openClientDetail = (client: Client) => {
    setSelectedClient(client);
    setClientDetailOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Pending':
        return <Clock className="h-4 w-4 text-gray-500" />;
      case 'Overdue':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Calculate statistics
  const stats = {
    total: clients.length,
    completed: clients.filter(c => c.basStatus === 'Completed').length,
    inProgress: clients.filter(c => c.basStatus === 'In Progress').length,
    pending: clients.filter(c => c.basStatus === 'Pending').length,
  };

  // Home Tab Content
  const HomeContent = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Clients</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Completed</p>
              <p className="text-2xl font-bold">{stats.completed}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-200" />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Recent Activity</h3>
        <div className="space-y-3">
          {filteredClients.slice(0, 5).map((client) => (
            <div 
              key={client.id}
              onClick={() => openClientDetail(client)}
              className="bg-card p-4 rounded-xl border border-border hover:bg-accent/50 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium">{client.companyName}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    {getStatusIcon(client.basStatus)}
                    {client.basStatus}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            className="h-auto p-4 flex-col gap-2"
            onClick={() => setActiveTab('clients')}
          >
            <Plus className="h-6 w-6" />
            <span>Add Client</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto p-4 flex-col gap-2"
            onClick={() => setActiveTab('stats')}
          >
            <BarChart3 className="h-6 w-6" />
            <span>View Stats</span>
          </Button>
        </div>
      </div>
    </div>
  );

  // Clients Tab Content
  const ClientsContent = () => (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-12">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="In Progress">In Progress</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Client List */}
      <div className="space-y-3">
        {filteredClients.map((client) => (
          <div 
            key={client.id}
            onClick={() => openClientDetail(client)}
            className="bg-card p-4 rounded-xl border border-border hover:bg-accent/50 transition-colors cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{client.companyName}</p>
                    <p className="text-sm text-muted-foreground">{client.directorName}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <StatusBadge status={client.basStatus} />
                  <StatusBadge status={client.bookkeepingStatus} />
                  <Badge variant="outline" className="text-xs">
                    {client.team}
                  </Badge>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground mt-1" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Stats Tab Content
  const StatsContent = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Dashboard Statistics</h2>
      
      <div className="space-y-4">
        <div className="bg-card p-4 rounded-xl border border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Client Status Overview</h3>
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Completed</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${(stats.completed / stats.total) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{stats.completed}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">In Progress</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full"
                    style={{ width: `${(stats.inProgress / stats.total) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{stats.inProgress}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Pending</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gray-500 rounded-full"
                    style={{ width: `${(stats.pending / stats.total) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-medium">{stats.pending}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card p-4 rounded-xl border border-border text-center">
            <Users className="h-8 w-8 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Clients</p>
          </div>
          <div className="bg-card p-4 rounded-xl border border-border text-center">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.completed}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Settings Tab Content
  const SettingsContent = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Settings</h2>
      
      <div className="space-y-4">
        <div className="bg-card p-4 rounded-xl border border-border">
          <h3 className="font-semibold mb-3">Account</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Admin User</p>
                  <p className="text-sm text-muted-foreground">admin@taxready.com</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </div>

        <div className="bg-card p-4 rounded-xl border border-border">
          <h3 className="font-semibold mb-3">Preferences</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span>Notifications</span>
              <div className="w-12 h-6 bg-primary rounded-full relative">
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span>Dark Mode</span>
              <div className="w-12 h-6 bg-gray-300 rounded-full relative">
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button variant="outline" className="w-full justify-start">
            <FileText className="h-4 w-4 mr-2" />
            Terms & Conditions
          </Button>
          <Button variant="outline" className="w-full justify-start">
            <FileText className="h-4 w-4 mr-2" />
            Privacy Policy
          </Button>
          <Button variant="outline" className="w-full justify-start text-red-500 hover:text-red-600">
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-16">
      {/* Mobile Header */}
      <header className="sticky top-0 z-40 bg-primary text-primary-foreground shadow-lg">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-primary-foreground">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72">
                  <SheetHeader>
                    <SheetTitle>Tax Ready</SheetTitle>
                    <SheetDescription>Client Management System</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-2">
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start"
                      onClick={() => { setActiveTab('home'); setMobileMenuOpen(false); }}
                    >
                      <Home className="h-4 w-4 mr-2" />
                      Home
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start"
                      onClick={() => { setActiveTab('clients'); setMobileMenuOpen(false); }}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Clients
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start"
                      onClick={() => { setActiveTab('stats'); setMobileMenuOpen(false); }}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Statistics
                    </Button>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start"
                      onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              <div>
                <h1 className="text-lg font-bold">Tax Ready</h1>
                <p className="text-xs text-primary-foreground/70">Mobile</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="text-primary-foreground">
                <Bell className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-20">
        <div className="px-4 py-6">
          {activeTab === 'home' && <HomeContent />}
          {activeTab === 'clients' && <ClientsContent />}
          {activeTab === 'stats' && <StatsContent />}
          {activeTab === 'settings' && <SettingsContent />}
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-40">
        <div className="grid grid-cols-4 h-16">
          <button
            onClick={() => setActiveTab('home')}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === 'home' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Home className="h-5 w-5" />
            <span className="text-xs">Home</span>
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === 'clients' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Users className="h-5 w-5" />
            <span className="text-xs">Clients</span>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === 'stats' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Stats</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              activeTab === 'settings' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Settings className="h-5 w-5" />
            <span className="text-xs">Settings</span>
          </button>
        </div>
      </nav>

      {/* Client Detail Modal */}
      <Dialog open={clientDetailOpen} onOpenChange={setClientDetailOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          {selectedClient && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedClient.companyName}</DialogTitle>
                <DialogDescription>Client Details & Status</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Client Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{selectedClient.companyName}</p>
                      <p className="text-sm text-muted-foreground">{selectedClient.directorName}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedClient.emailAddress}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>Not provided</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>ABN: {selectedClient.abn}</span>
                    </div>
                  </div>
                </div>

                {/* Status Overview */}
                <div>
                  <h4 className="font-semibold mb-3">Task Status</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <span className="text-sm">BAS Status</span>
                      <StatusBadge status={selectedClient.basStatus} />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <span className="text-sm">Bookkeeping</span>
                      <StatusBadge status={selectedClient.bookkeepingStatus} />
                    </div>
                    <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                      <span className="text-sm">Payroll</span>
                      <StatusBadge status={selectedClient.payrollPayslip} />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Status Report
                  </Button>
                  <Button variant="outline" className="w-full">
                    Edit Client
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileApp;